import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import List, { STATE } from "../../List";
import Button from "../../common/Button";
import TextBox from "../../common/TextBox";
import { Form, Formik } from "formik";
import ComboBox from "../../common/ComboBox";
import { ReactComponent as SearchSvg } from "../../../assets/tendering/magnifying-glass.svg";
import useTendering from "../../../hooks/useTendering";
import { useNavigate } from "react-router-dom";
import { ReactComponent as EditSvg } from "../../../assets/profile/edit.svg";
import { ReactComponent as ShareSvg } from "../../../assets/profile/share.svg";
import { ReactComponent as DeleteSvg } from "../../../assets/profile/delete.svg";
import { ReactComponent as WarningSvg } from "../../../assets/icons/warning.svg";
import { ReactComponent as FilterSvg } from "../../../assets/icons/filter-primary.svg";
import useUsers from "../../../hooks/useUsers";
import useCompanies from "../../../hooks/useCompanies";
import * as Tooltip from "react-tooltip";
import SharePopup from "../../Tendering/SharePopup";
import { provinces } from "../../../helpers/common.helper";
import Overlay from "../../Overlay";
import { format } from "date-fns";
import SecondaryPagination from "../../SecondaryPagination/SecondaryPagination";
import AdvancedFilter from "./AdvancedFilter";
import ProfileContext from "../ProfileContext";
import AppContext from "../../../AppContext";
import _ from "lodash";

const assetsUri = import.meta.env.ASSETS_URI;

const TABS = {
  MY_TENDERINGS: 0,
  PARTICIPATING: 1,
  WISHLISTS: 2,
};

const templateColumn = [
  {
    display: "Name",
    key: "name",
    textAlign: "left",
  },
  {
    display: "Location",
    key: "location",
    textAlign: "left",
  },
  {
    display: "Owner",
    key: "owner",
    textAlign: "left",
  },
  {
    display: "Type",
    key: "type",
    textAlign: "left",
  },
  {
    display: "End date",
    key: "dueDate",
    textAlign: "left",
    minWidth: "90px",
  },
  {
    display: "",
    key: "actions",
    textAlign: "left",
  },
];

const Tenderings = (props) => {
  const { isCompany, isOwnerCompany, company, user, viewMode, isOwner } =
    useContext(ProfileContext);
  const { isAllowed } = useContext(AppContext);
  const [currentTab, setCurrentTab] = useState(TABS.MY_TENDERINGS);
  const [dataSource, setDataSource] = useState([]);
  const [fetchingState, setFetchingState] = useState(STATE.LOADING);
  const navigate = useNavigate();
  const { query, deleteById } = useTendering();
  const { queryUsers } = useUsers();
  const { queryCompany } = useCompanies();
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [metadata, setMetadata] = useState(undefined);
  const [isToggleShareModal, setIsToggleShareModal] = useState(undefined);
  const [isDeleteModal, setIsDeleteModal] = useState(undefined);
  const [isToggleFilter, setIsToggleFilter] = useState(undefined);
  const formikRef = useRef(null);
  const [filter, setFilter] = useState(undefined);

  const columns = useMemo(() => {
    let _columns = _.cloneDeep(templateColumn);

    if (currentTab === TABS.MY_TENDERINGS) {
      _columns = _columns.filter((c) => c.key !== "owner");
    }

    if (currentTab !== TABS.MY_TENDERINGS) {
      _columns = _columns.filter((c) => c.key !== "actions");
    }

    if (!isOwner) {
      _columns = _columns.filter((c) => c.key !== "actions");
    }

    if (viewMode === "guest") {
      _columns = _columns.filter((c) => c.key !== "actions");
    }

    if (isCompany && !isAllowed("CM-03-0000", "create")) {
      _columns = _columns.filter((c) => c.key !== "actions");
    }

    return _columns;
  }, [currentTab]);

  const _refetch = async (_limit, _page) => {
    try {
      const response = await query(
        filter,
        _page ?? page,
        _limit ?? limit,
        null,
        null
      );
      const { _metadata, data } = response;

      setMetadata(_metadata);

      const _datasource = [];
      let _users = [];
      let _companies = [];
      const _userIds =
        currentTab !== TABS.MY_TENDERINGS
          ? data
              .map((d) => d.createdBy)
              .filter((value, index, array) => {
                return array.indexOf(value) === index;
              })
          : [];
      const _companyIds =
        currentTab !== TABS.MY_TENDERINGS
          ? data
              .map((d) => d.company)
              .filter((value, index, array) => {
                return !!value && array.indexOf(value) === index;
              })
          : [];

      // * Get owner data if the tab is participating or wishlist
      if (currentTab !== TABS.MY_TENDERINGS) {
        const { data: usersFound } = await queryUsers(
          { _id: { $in: _userIds } },
          null,
          -1,
          { _id: 1, avatar: 1, firstname: 1, surname: 1, uri: 1 }
        );

        _users = usersFound;

        if (_companyIds.length > 0) {
          const { data: companyFound } = await queryCompany(
            { _id: { $in: _userIds } },
            null,
            -1,
            { _id: 1, logo: 1, name: 1, uri: 1 }
          );

          _companies = companyFound;
        }
      }

      // * Get actions
      let actions = <></>;

      for (const d of data) {
        let _data = {
          _id: d._id,
          name: (
            <div className="profile__tenderings__list__item__name">
              <img
                className="profile__tenderings__list__item__name__cover"
                src={`${assetsUri}/${
                  d.images.cover.cropped ?? d.images.cover.original
                }`}
              />
              {d.name}
            </div>
          ),
          location: d.location,
          type: d.category === 1 ? "Design" : "Construction",
          dueDate: format(new Date(d.period?.end), "dd MMM yyyy"),
          status: (
            <div className="profile__tenderings__list__item__status">
              {d.status}
            </div>
          ),
          actions: (
            <div className="profile__tenderings__list__item__actions">
              <EditSvg
                className="profile__tenderings__list__item__actions__action"
                width={24}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/tenderings/${d._id}/edit`);
                }}
              />
              <ShareSvg
                className="profile__tenderings__list__item__actions__action"
                width={24}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsToggleShareModal(
                    `${window.location.origin}/tenderings/${d._id}`
                  );
                }}
              />
              <DeleteSvg
                className="profile__tenderings__list__item__actions__action"
                width={24}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteModal(d);
                }}
              />
            </div>
          ),
          owner: <></>,
        };

        // * Map company if exists
        if (currentTab !== TABS.MY_TENDERINGS && d.company) {
          let _company = _companies.find((c) => c._id);

          _data.owner = (
            <img
              className="profile__tenderings__list__item__owner"
              src={`${assetsUri}/${_company.logo}`}
              width={32}
              data-tooltip-id="my-tooltip"
              data-tooltip-content={_company.name}
              onClick={() => navigate(`/companies/${_company._id}`)}
            />
          );
        }

        // * Map user instead if company doesn't exists
        if (currentTab !== TABS.MY_TENDERINGS && !d.company) {
          let _user = _users.find((u) => u._id);

          _data.owner = (
            <img
              className="profile__tenderings__list__item__owner"
              src={`${assetsUri}/${_user.avatar}`}
              width={32}
              data-tooltip-id="my-tooltip"
              data-tooltip-content={`${_user.firstname}${
                _user.surname ? ` ${_user.surname}` : ""
              }`}
              onClick={() => navigate("/" + _user.uri)}
            />
          );
        }

        _datasource.push(_data);
      }

      setDataSource(_datasource);

      setFetchingState(STATE.IDLE);
    } catch (error) {
      console.error(error);
      setFetchingState(STATE.ERROR);
    }
  };

  useEffect(() => {
    setFetchingState(STATE.LOADING);
    formikRef.current.resetForm();
    let newFilter = _getFilterPredicate();

    setFilter(newFilter);
  }, [currentTab]);

  useEffect(() => {
    if (!filter) return;

    setFetchingState(STATE.LOADING);
    _refetch();
  }, [filter]);

  const _getTabClassname = (tab) => {
    if (tab === currentTab) {
      return [
        "profile__tenderings__tabs__tab",
        "profile__tenderings__tabs__tab--active",
      ].join(" ");
    }

    return "profile__tenderings__tabs__tab";
  };

  const handleCreateClick = () => {
    navigate("/tenderings/create");
  };

  const handleDelete = async (id) => {
    await deleteById(id);
    setIsDeleteModal(undefined);
  };

  const _getModalDelete = () => {
    // * Get work from datasource
    const _work = isDeleteModal;

    const prefix = "profile-project-owner";

    return (
      <>
        <Overlay
          onClose={() => {
            setIsDeleteModal(undefined);
          }}
          style={{
            alignItems: "flex-start",
          }}
        >
          <div className={`${prefix}-delete`}>
            <WarningSvg width={64} />
            <p className={`${prefix}-delete__header`}>
              {`Delete ${_work.name}?`}
            </p>
            <div className={`${prefix}-delete__action`}>
              <Button
                variant={"secondary"}
                label={"Cancel"}
                onClick={() => setIsDeleteModal(undefined)}
              />
              <Button
                label={"Confirm"}
                onClick={() => handleDelete(_work._id)}
              />
            </div>
          </div>
        </Overlay>
      </>
    );
  };

  const _getFilterPredicate = (values) => {
    const { name, location, status } = values ?? {};

    let _filter = { active: true, deleted: false };

    if (name) {
      _filter.name = {
        $regex: name,
      };
    }

    if (location && location.length > 0) {
      _filter.location = {
        $in: location,
      };
    }

    if (status && status.length > 0) {
      _filter.status = {
        $in: status,
      };
    }

    if (currentTab === TABS.MY_TENDERINGS) {
      if (isCompany) {
        _filter.company = company._id;
      }

      if (!isCompany) {
        _filter.createdBy = user._id;
      }
    }

    if (currentTab === TABS.PARTICIPATING) {
      _filter["participants.user"] = user._id;
    }

    return _filter;
  };

  const handleFilterSubmit = (values, { setSubmitting }) => {
    setSubmitting(false);
    setFilter(_getFilterPredicate(values));
  };

  const _getCreateButton = () => {
    if (currentTab !== TABS.MY_TENDERINGS) {
      return <></>;
    }

    if (viewMode === "guest") return <></>;

    if (isCompany && isAllowed("CM-03-0000", "create")) {
      return <Button label="Create tendering" onClick={handleCreateClick} />;
    }

    if (!isCompany && isOwner && _.isEmpty(user?.company)) {
      return <Button label="Create tendering" onClick={handleCreateClick} />;
    }

    return <></>;
  };

  return (
    <div className="profile__tenderings">
      <div className="profile__tenderings__tabs">
        <div
          className={_getTabClassname(TABS.MY_TENDERINGS)}
          onClick={() => setCurrentTab(TABS.MY_TENDERINGS)}
        >
          My Tenderings
        </div>
        <div
          className={_getTabClassname(TABS.PARTICIPATING)}
          onClick={() => setCurrentTab(TABS.PARTICIPATING)}
        >
          Participating
        </div>
        <div
          className={_getTabClassname(TABS.WISHLISTS)}
          onClick={() => setCurrentTab(TABS.WISHLISTS)}
        >
          Wishlist
        </div>
      </div>
      <div className="profile__tenderings__sub">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {_getCreateButton()}
          <div
            // className="shopping-container-filter-advanced"
            className="advanced-filter__trigger"
            onClick={() => setIsToggleFilter((curr) => !curr)}
          >
            <FilterSvg width={24} />
            Filter
          </div>
        </div>
        <Formik
          innerRef={formikRef}
          initialValues={{
            name: undefined,
            location: [],
            status: [],
          }}
          onSubmit={handleFilterSubmit}
        >
          <Form className="profile__tenderings__sub__filter">
            <TextBox name="name" label="Project name" />
            <ComboBox
              name="location"
              label="Location"
              multi={true}
              options={provinces}
              width={200}
            />
            <ComboBox
              name="status"
              label="Status"
              multi={true}
              options={[{ name: "Announcement", value: 1 }]}
              width={200}
            />
            <div style={{ flexGrow: 1 }} />
            <Button label="Search" leadingIcon={<SearchSvg />} type="submit" />
          </Form>
        </Formik>
      </div>
      <div className="profile__tenderings__list">
        <List
          columns={columns}
          dataSource={dataSource}
          contentState={fetchingState}
          viewable={true}
          scrollable={true}
          onRecordClick={(record) => navigate(`/tenderings/${record._id}`)}
        >
          <SecondaryPagination
            defaultLimit={limit}
            limits={[1, 10, 50]}
            pages={metadata?.totalPages || 1}
            onPageChange={(page) => {
              setPage(page);
              setFetchingState(STATE.LOADING);
              _refetch(undefined, page);
            }}
            onLimitChange={(limit) => {
              setLimit(limit);
              setPage(1);
              setFetchingState(STATE.LOADING);
              _refetch(limit, 1);
            }}
          />
        </List>
        <Tooltip.Tooltip id="my-tooltip" />
        {isToggleShareModal && (
          <SharePopup
            link={isToggleShareModal}
            onClose={() => setIsToggleShareModal(undefined)}
          />
        )}
        {isDeleteModal && _getModalDelete()}
        {isToggleFilter && (
          <AdvancedFilter
            defaultValue={formikRef.current.values}
            onClose={() => setIsToggleFilter(false)}
            onChange={(values) => {
              for (const key in values) {
                if (!Object.prototype.hasOwnProperty.call(values, key))
                  continue;
                formikRef.current.setFieldValue(key, values[key]);
              }
              setFilter(_getFilterPredicate(values));
              setIsToggleFilter(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

Tenderings.propTypes = {};

export default Tenderings;
